import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, XCircle, ChevronDown, ChevronRight } from "lucide-react";

const CollapsibleCode = ({ title, code }) => {
    const [open, setOpen] = useState(false);
    const Icon = open ? ChevronDown : ChevronRight;
    if (!code) return null;

    return (
        <div className="border rounded-lg bg-white">
            <button onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2 p-3 text-left hover:bg-slate-50 font-medium text-sm">
                <Icon className="h-4 w-4 text-slate-400" />{title}
            </button>
            {open && (
                <div className="border-t p-3">
                    <pre className="text-xs font-mono whitespace-pre-wrap bg-slate-900 text-green-400 p-3 rounded max-h-[500px] overflow-auto">
                        {typeof code === "string" ? code : JSON.stringify(code, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
};

export default function AgentResultTab({ data }) {
    const { t } = useTranslation();
    const result = data?.agentResult;

    if (!result) {
        return <p className="text-muted-foreground text-sm p-4">{t("admin.automationPlayground.noResult")}</p>;
    }

    const isSuccess = result.success !== false;
    const isSearchApis = data.agentType === "search-apis";

    return (
        <div className="space-y-3 mt-3">
            <div className={`flex items-center gap-2 p-3 rounded-lg border ${isSuccess ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                {isSuccess ? <CheckCircle className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-red-500" />}
                <span className={`text-sm font-medium ${isSuccess ? "text-green-700" : "text-red-700"}`}>
                    {isSuccess ? t("admin.automationPlayground.agentSuccess") : t("admin.automationPlayground.agentFailed")}
                </span>
            </div>

            {isSearchApis && result.objective && (
                <div className="bg-white border rounded-lg p-3">
                    <p className="text-xs font-medium text-slate-500 mb-1">{t("admin.automationPlayground.objective")}</p>
                    <p className="text-sm">{result.objective}</p>
                </div>
            )}

            {(result.answer || result.validationError) && (
                <div className="bg-white border rounded-lg p-3">
                    <p className="text-xs font-medium text-slate-500 mb-1">
                        {isSearchApis ? t("admin.automationPlayground.recommendation") : t("admin.automationPlayground.answer")}
                    </p>
                    <pre className="text-sm whitespace-pre-wrap font-mono bg-slate-50 p-3 rounded max-h-[500px] overflow-auto">
                        {result.answer || result.validationError}
                    </pre>
                </div>
            )}

            {isSearchApis && result.promptsSearched > 0 && (
                <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white border rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold">{result.promptsSearched}</p>
                        <p className="text-xs text-slate-500">{t("admin.automationPlayground.promptsSearched")}</p>
                    </div>
                    <div className="bg-white border rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold">{result.connectedPrompts ?? 0}</p>
                        <p className="text-xs text-slate-500">{t("admin.automationPlayground.connectedApis")}</p>
                    </div>
                    <div className="bg-white border rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold">{result.unconnectedPrompts ?? 0}</p>
                        <p className="text-xs text-slate-500">{t("admin.automationPlayground.unconnectedApis")}</p>
                    </div>
                </div>
            )}

            <CollapsibleCode title={t("admin.automationPlayground.rawResult")} code={result} />
        </div>
    );
}
